import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  BadgeCheck,
  CircleSlash2,
  PanelsTopLeft,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import {
  api,
  apiBase,
  type Application,
  type GoogleProviderConfig,
} from "@/lib/api";
import {
  GOOGLE_STATUS_COPY,
  REDIRECT_CORS_COPY,
  SCOPE_COPY,
  applicationToForm,
  buildApplicationInput,
  emptyApplicationForm,
  hasDangerousApplicationChanges,
  type ApplicationFormValues,
  type ApplicationScope,
} from "@/lib/applications";
import { ApplicationBrandingPreview } from "@/components/ApplicationBrandingPreview";
import {
  EmptyState,
  ErrorBox,
  PageHeader,
  Spinner,
  SuccessBox,
  WarnBox,
} from "@/components/ui";
import { Badge } from "@/components/ui/badge";

export function ApplicationsPage() {
  const { id = "", applicationId } = useParams<{
    id: string;
    applicationId?: string;
  }>();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [selected, setSelected] = useState<Application | null>(null);
  const [projectName, setProjectName] = useState("Current Project");
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const [projectResponse, listResponse, detailResponse] =
          await Promise.all([
            api.getProject(id),
            api.listApplications(id),
            applicationId
              ? api.getApplication(id, applicationId)
              : Promise.resolve(null),
          ]);
        setProjectName(projectResponse.data.name);
        setApplications(listResponse.data);
        setSelected(detailResponse?.data ?? null);
      } catch (caught) {
        setError(caught);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, applicationId]);

  if (loading) return <Spinner label="Loading Applications…" />;
  if (error) return <ErrorBox error={error} />;

  if (applicationId && selected) {
    return (
      <ApplicationEditor
        projectId={id}
        projectName={projectName}
        application={selected}
        onSaved={(application) => {
          setSelected(application);
          setApplications((items) =>
            items.map((item) =>
              item.id === application.id ? application : item,
            ),
          );
        }}
        onDeleted={() => navigate(`/dashboard/projects/${id}/applications`)}
      />
    );
  }

  if (creating) {
    return (
      <ApplicationEditor
        projectId={id}
        projectName={projectName}
        application={null}
        onCancel={() => setCreating(false)}
        onSaved={(application) => {
          setApplications((items) => [application, ...items]);
          navigate(`/dashboard/projects/${id}/applications/${application.id}`);
        }}
      />
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Hosted Auth"
        title="Applications"
        description="Register browser clients for hosted sign-in. Each Application has exact callbacks, bounded identity scopes, and its own safe branding."
        actions={
          <button
            type="button"
            className="btn-primary"
            onClick={() => setCreating(true)}
          >
            <Plus className="h-4 w-4" aria-hidden /> New Application
          </button>
        }
      />
      <div className="mb-5">
        <WarnBox>{REDIRECT_CORS_COPY}</WarnBox>
      </div>
      {applications.length === 0 ? (
        <EmptyState
          title="No Applications registered"
          icon={<PanelsTopLeft className="h-5 w-5" />}
        >
          Register the first Web that will redirect users to TinyBase Hosted
          Auth.
          <button
            type="button"
            className="btn-primary mt-4"
            onClick={() => setCreating(true)}
          >
            Register Application
          </button>
        </EmptyState>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {applications.map((application) => (
            <Link
              key={application.id}
              to={`/dashboard/projects/${id}/applications/${application.id}`}
              className="card group block p-5 hover:border-border-strong"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 font-sans font-semibold text-text-primary">
                    <span className="truncate">{application.name}</span>
                    {application.eVerify ? (
                      <BadgeCheck
                        className="h-4 w-4 shrink-0 text-secondary"
                        aria-label="Verified by TinyBase"
                      />
                    ) : null}
                  </div>
                  <div className="mt-1 truncate font-mono text-[11px] text-text-disabled">
                    {application.client_id}
                  </div>
                </div>
                <Badge
                  variant={
                    application.status === "active" ? "success" : "secondary"
                  }
                >
                  {application.status}
                </Badge>
              </div>
              <p className="mt-4 line-clamp-2 min-h-10 text-sm text-text-muted">
                {application.description || "No description yet."}
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {application.allowed_scopes.map((scope) => (
                  <span key={scope} className="mono-chip">
                    {scope}
                  </span>
                ))}
              </div>
              <div className="mt-4 border-t border-border-standard pt-3 text-xs text-secondary group-hover:text-secondary">
                Configure Application →
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function ApplicationEditor({
  projectId,
  projectName,
  application,
  onSaved,
  onDeleted,
  onCancel,
}: {
  projectId: string;
  projectName: string;
  application: Application | null;
  onSaved: (application: Application) => void;
  onDeleted?: () => void;
  onCancel?: () => void;
}) {
  const [values, setValues] = useState<ApplicationFormValues>(() =>
    application ? applicationToForm(application) : emptyApplicationForm(),
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues(
      application ? applicationToForm(application) : emptyApplicationForm(),
    );
    setFieldErrors({});
    setError(null);
    setMessage("");
  }, [application]);

  function setField<K extends keyof ApplicationFormValues>(
    field: K,
    value: ApplicationFormValues[K],
  ) {
    setValues((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMessage("");
    const result = buildApplicationInput(values);
    setFieldErrors(result.errors);
    if (!result.input) return;
    if (
      application &&
      hasDangerousApplicationChanges(application, result.input) &&
      !window.confirm(
        "Redirect, domain, or scope changes can break active sign-in flows and reset public verification. Save these changes?",
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      const response = application
        ? await api.updateApplication(projectId, application.id, result.input)
        : await api.createApplication(projectId, result.input);
      onSaved(response.data);
      setMessage(
        application ? "Application settings saved." : "Application registered.",
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled() {
    if (!application) return;
    const enabling = application.status !== "active";
    if (
      !enabling &&
      !window.confirm(
        "Disable this Application? New authorization and refresh flows will stop until it is enabled again.",
      )
    )
      return;
    setError(null);
    try {
      const response = await api.setApplicationEnabled(
        projectId,
        application.id,
        enabling,
      );
      onSaved(response.data);
      setMessage(enabling ? "Application enabled." : "Application disabled.");
    } catch (caught) {
      setError(caught);
    }
  }

  async function remove() {
    if (!application) return;
    if (
      !window.confirm(
        `Delete ${application.name}? This removes the client from owner views and blocks future authorization. This action cannot be undone from the Dashboard.`,
      )
    )
      return;
    setError(null);
    try {
      await api.deleteApplication(projectId, application.id);
      onDeleted?.();
    } catch (caught) {
      setError(caught);
    }
  }

  async function toggleVerificationReview() {
    if (!application || application.eVerify) return;
    const cancelling = application.review_status === "pending";
    if (
      !window.confirm(
        cancelling
          ? "Cancel the pending verification review?"
          : "Submit the current Application identity, domains, callbacks, scopes, and branding for operator review? Material changes will cancel the review.",
      )
    )
      return;
    setError(null);
    try {
      const response = cancelling
        ? await api.cancelApplicationVerification(projectId, application.id)
        : await api.requestApplicationVerification(projectId, application.id);
      onSaved(response.data);
      setMessage(
        cancelling
          ? "Verification review cancelled."
          : "Verification review requested.",
      );
    } catch (caught) {
      setError(caught);
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Hosted Auth / Application"
        title={application ? application.name : "Register Application"}
        description={
          application
            ? "Configure identity, callbacks, permissions, branding, and public-client security."
            : "Create a public browser client. TinyBase never issues a client secret to this Application."
        }
        actions={
          <>
            <Link
              className="btn-secondary"
              to={`/dashboard/projects/${projectId}/applications`}
              onClick={onCancel}
            >
              ← Applications
            </Link>
            {application ? (
              <Badge
                variant={
                  application.status === "active" ? "success" : "secondary"
                }
              >
                {application.status}
              </Badge>
            ) : null}
          </>
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      {message ? (
        <div className="mb-4">
          <SuccessBox>{message}</SuccessBox>
        </div>
      ) : null}

      <form onSubmit={save} noValidate className="space-y-6">
        {application ? (
          <ReadOnlyTrust
            application={application}
            onToggleReview={() => void toggleVerificationReview()}
          />
        ) : null}

        <FormSection
          title="Identity and developer"
          description="Public metadata shown on hosted sign-in and consent pages."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="application-name"
              label="Application name"
              value={values.name}
              error={fieldErrors.name}
              required
              onChange={(value) => setField("name", value)}
            />
            <TextField
              id="application-slug"
              label="Application slug"
              value={values.slug}
              error={fieldErrors.slug}
              required
              hint="Lowercase letters, numbers, and hyphens."
              onChange={(value) => setField("slug", value)}
            />
            <TextField
              id="developer-name"
              label="Developer name"
              value={values.developerName}
              error={fieldErrors.developerName}
              onChange={(value) => setField("developerName", value)}
            />
            <TextField
              id="support-contact"
              label="Support contact"
              value={values.supportContact}
              error={fieldErrors.supportContact}
              placeholder="support@example.com"
              onChange={(value) => setField("supportContact", value)}
            />
          </div>
          <TextArea
            id="application-description"
            label="Description"
            value={values.description}
            error={fieldErrors.description}
            rows={3}
            onChange={(value) => setField("description", value)}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="homepage-url"
              label="Homepage URL"
              type="url"
              value={values.homepageUrl}
              error={fieldErrors.homepageUrl}
              onChange={(value) => setField("homepageUrl", value)}
            />
            <TextField
              id="developer-url"
              label="Developer URL"
              type="url"
              value={values.developerUrl}
              error={fieldErrors.developerUrl}
              onChange={(value) => setField("developerUrl", value)}
            />
            <TextField
              id="privacy-url"
              label="Privacy policy URL"
              type="url"
              value={values.privacyPolicyUrl}
              error={fieldErrors.privacyPolicyUrl}
              onChange={(value) => setField("privacyPolicyUrl", value)}
            />
            <TextField
              id="terms-url"
              label="Terms URL"
              type="url"
              value={values.termsUrl}
              error={fieldErrors.termsUrl}
              onChange={(value) => setField("termsUrl", value)}
            />
            <TextField
              id="logo-url"
              label="Light logo URL"
              type="url"
              value={values.logoUrl}
              error={fieldErrors.logoUrl}
              onChange={(value) => setField("logoUrl", value)}
            />
            <TextField
              id="logo-dark-url"
              label="Dark logo URL"
              type="url"
              value={values.logoDarkUrl}
              error={fieldErrors.logoDarkUrl}
              onChange={(value) => setField("logoDarkUrl", value)}
            />
          </div>
        </FormSection>

        <FormSection
          title="Authorization callbacks and domains"
          description={REDIRECT_CORS_COPY}
        >
          <div className="grid gap-4 lg:grid-cols-2">
            <TextArea
              id="redirect-uris"
              label="Exact redirect URIs"
              value={values.redirectUris}
              error={fieldErrors.redirectUris}
              rows={6}
              required
              hint="One URL per line. HTTPS required except explicit-port localhost/127.0.0.1."
              onChange={(value) => setField("redirectUris", value)}
            />
            <TextArea
              id="declared-domains"
              label="Declared domains"
              value={values.declaredDomains}
              error={fieldErrors.declaredDomains}
              rows={6}
              hint="One exact origin per line; no paths or wildcards."
              onChange={(value) => setField("declaredDomains", value)}
            />
          </div>
        </FormSection>

        <FormSection
          title="Identity scopes"
          description="Scopes describe identity data only. They never bypass PostgreSQL RLS or Project permissions."
        >
          <div className="grid gap-3 sm:grid-cols-3">
            {(Object.keys(SCOPE_COPY) as ApplicationScope[]).map((scope) => (
              <label
                key={scope}
                className="flex cursor-pointer gap-3 rounded-lg border border-border-standard bg-surface-container-low p-3 text-sm"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-border-standard text-secondary focus:ring-secondary"
                  checked={values.allowedScopes.includes(scope)}
                  onChange={(event) =>
                    setField(
                      "allowedScopes",
                      event.target.checked
                        ? [...values.allowedScopes, scope]
                        : values.allowedScopes.filter(
                            (value) => value !== scope,
                          ),
                    )
                  }
                />
                <span>
                  <span className="block font-mono text-xs font-semibold text-text-primary">
                    {scope}
                  </span>
                  <span className="mt-1 block text-xs text-text-muted">
                    {SCOPE_COPY[scope]}
                  </span>
                </span>
              </label>
            ))}
          </div>
          {fieldErrors.allowedScopes ? (
            <FieldError message={fieldErrors.allowedScopes} />
          ) : null}
        </FormSection>

        <FormSection
          title="Branding and hosted-page preview"
          description="Only validated colors and image URLs are accepted. Custom HTML, CSS, and JavaScript are never rendered."
        >
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,1fr)]">
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {(
                  [
                    ["primary_color", "Primary action"],
                    ["accent_color", "Accent / fallback"],
                    ["background_color", "Page background"],
                    ["text_color", "Text"],
                  ] as const
                ).map(([field, label]) => (
                  <ColorField
                    key={field}
                    id={`branding-${field}`}
                    label={label}
                    value={values.branding[field]}
                    onChange={(value) =>
                      setField("branding", {
                        ...values.branding,
                        [field]: value,
                      })
                    }
                  />
                ))}
              </div>
              <div>
                <label className="label" htmlFor="branding-display-mode">
                  Display mode
                </label>
                <select
                  id="branding-display-mode"
                  className="input"
                  value={values.branding.display_mode}
                  onChange={(event) =>
                    setField("branding", {
                      ...values.branding,
                      display_mode: event.target.value as "card" | "centered",
                    })
                  }
                >
                  <option value="card">Card</option>
                  <option value="centered">Centered</option>
                </select>
              </div>
              {fieldErrors.branding ? (
                <FieldError message={fieldErrors.branding} />
              ) : null}
              <WarnBox>
                Application branding cannot cover or imitate TinyBase trust
                indicators, verification badges, scope text, or consent
                controls.
              </WarnBox>
            </div>
            <ApplicationBrandingPreview
              values={values}
              eVerify={application?.eVerify === true}
              projectName={projectName}
            />
          </div>
        </FormSection>

        <FormSection
          title="Public-client security"
          description="PKCE S256 is mandatory. Token and session lifetimes stay within platform safety bounds."
        >
          <div className="mb-4 flex items-center gap-2 rounded-md border border-success bg-success-container px-3 py-2 text-sm text-success">
            <ShieldCheck className="h-4 w-4" aria-hidden /> PKCE required (S256
            only)
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <NumberField
              id="access-ttl"
              label="Access token TTL (seconds)"
              value={values.accessTokenTTLSeconds}
              min={300}
              max={3600}
              error={fieldErrors.accessTokenTTLSeconds}
              onChange={(value) => setField("accessTokenTTLSeconds", value)}
            />
            <NumberField
              id="refresh-ttl"
              label="Refresh token TTL (seconds)"
              value={values.refreshTokenTTLSeconds}
              min={3600}
              max={2592000}
              error={fieldErrors.refreshTokenTTLSeconds}
              onChange={(value) => setField("refreshTokenTTLSeconds", value)}
            />
            <NumberField
              id="session-ttl"
              label="Session TTL (seconds)"
              value={values.sessionTTLSeconds}
              min={3600}
              max={2592000}
              error={fieldErrors.sessionTTLSeconds}
              onChange={(value) => setField("sessionTTLSeconds", value)}
            />
          </div>
          {application ? (
            <GoogleProviderPanel
              projectId={projectId}
              applicationId={application.id}
            />
          ) : (
            <div className="mt-5 rounded-lg border border-border-standard bg-surface-container-low p-4">
              <div className="text-sm font-semibold text-text-primary">
                Google sign-in
              </div>
              <p className="mt-1 text-xs text-text-muted">
                Register the Application before configuring Google.{" "}
                {GOOGLE_STATUS_COPY}
              </p>
            </div>
          )}
        </FormSection>

        <div className="sticky bottom-3 z-sticky flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border-standard bg-surface-container-lowest p-3 shadow-none">
          <span className="text-xs text-text-muted">
            Server validation remains authoritative. Invalid input stays in this
            form for correction.
          </span>
          <div className="flex gap-2">
            {onCancel ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={onCancel}
              >
                Cancel
              </button>
            ) : null}
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving
                ? "Saving…"
                : application
                  ? "Save settings"
                  : "Register Application"}
            </button>
          </div>
        </div>
      </form>

      {application ? (
        <section className="mt-8 rounded-lg border border-error bg-error-container p-5">
          <h2 className="font-sans font-semibold text-error">
            Danger zone
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Disabling is reversible. Deleting soft-removes the client from owner
            views and blocks future authorization.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void toggleEnabled()}
            >
              <CircleSlash2 className="h-4 w-4" aria-hidden />{" "}
              {application.status === "active"
                ? "Disable Application"
                : "Enable Application"}
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => void remove()}
            >
              <Trash2 className="h-4 w-4" aria-hidden /> Delete Application
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function GoogleProviderPanel({
  projectId,
  applicationId,
}: {
  projectId: string;
  applicationId: string;
}) {
  const defaultRedirect = `${apiBase()}/portal/providers/google/callback`;
  const [config, setConfig] = useState<GoogleProviderConfig | null>(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [redirectUri, setRedirectUri] = useState(defaultRedirect);
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    void api
      .getGoogleProviderConfig(projectId, applicationId)
      .then(({ data }) => {
        setConfig(data);
        setClientId(data.client_id ?? "");
        setRedirectUri(data.redirect_uri || defaultRedirect);
        setEnabled(data.configured ? data.enabled : true);
      })
      .catch(setError);
  }, [applicationId, defaultRedirect, projectId]);

  async function saveGoogle() {
    setBusy(true);
    setError(null);
    setMessage("");
    try {
      const { data } = await api.updateGoogleProviderConfig(
        projectId,
        applicationId,
        {
          client_id: clientId,
          client_secret: clientSecret || undefined,
          redirect_uri: redirectUri,
          enabled,
        },
      );
      setConfig(data);
      setClientSecret("");
      setMessage("Google configuration saved. The secret remains write-only.");
    } catch (reason) {
      setError(reason);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-5 space-y-4 rounded-lg border border-border-standard bg-surface-container-low p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-text-primary">Google sign-in</div>
          <p className="mt-1 text-xs text-text-muted">{GOOGLE_STATUS_COPY}</p>
        </div>
        <Badge variant="secondary">
          {config?.configured
            ? enabled
              ? "Enabled"
              : "Disabled"
            : "Not configured"}
        </Badge>
      </div>
      {error ? <ErrorBox error={error} /> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          id="google-client-id"
          label="Google client ID"
          value={clientId}
          error={undefined}
          onChange={setClientId}
        />
        <TextField
          id="google-client-secret"
          label={
            config?.configured
              ? "Replace client secret (optional)"
              : "Google client secret"
          }
          value={clientSecret}
          type="password"
          error={undefined}
          onChange={setClientSecret}
        />
      </div>
      <TextField
        id="google-redirect-uri"
        label="Exact Google callback URI"
        value={redirectUri}
        error={undefined}
        onChange={setRedirectUri}
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        Enable Google for this Application
      </label>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-secondary"
          disabled={busy}
          onClick={() => void saveGoogle()}
        >
          {busy ? "Saving…" : "Save Google configuration"}
        </button>
        {message ? <span className="text-xs text-secondary">{message}</span> : null}
      </div>
    </div>
  );
}

function ReadOnlyTrust({
  application,
  onToggleReview,
}: {
  application: Application;
  onToggleReview: () => void;
}) {
  return (
    <section className="card grid gap-4 p-5 md:grid-cols-[1fr_auto]">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-sans font-semibold text-text-primary">
            Client and verification
          </h2>
          {application.eVerify ? (
            <Badge className="gap-1 bg-secondary-fixed text-on-secondary-fixed">
              <BadgeCheck className="h-3.5 w-3.5" aria-hidden /> Verified by
              TinyBase
            </Badge>
          ) : (
            <Badge variant="secondary">Not verified</Badge>
          )}
          <Badge variant="outline">
            Review: {application.review_status.replace("_", " ")}
          </Badge>
        </div>
        <p className="mt-2 text-sm text-text-muted">
          Verification is read-only for Project owners. Only an authorized
          TinyBase operator can approve, deny, or revoke it; verification grants
          no scopes or permissions.
        </p>
        {!application.eVerify ? (
          <button
            type="button"
            className="btn-secondary mt-3"
            onClick={onToggleReview}
          >
            {application.review_status === "pending"
              ? "Cancel review request"
              : "Request verification review"}
          </button>
        ) : null}
      </div>
      <div className="min-w-0 md:text-right">
        <div className="eyebrow">Public client ID</div>
        <code className="mt-1 block max-w-full select-all overflow-x-auto rounded border border-border-standard bg-surface-container-low px-2 py-1.5 text-xs text-text-primary">
          {application.client_id}
        </code>
        <div className="mt-1 text-[11px] text-text-disabled">
          No client secret is issued.
        </div>
      </div>
      <dl className="grid gap-3 border-t border-border-standard pt-4 text-xs sm:grid-cols-3 md:col-span-2">
        <div>
          <dt className="eyebrow">Created</dt>
          <dd className="mt-1 text-text-secondary">
            {formatApplicationTime(application.created_at)}
          </dd>
        </div>
        <div>
          <dt className="eyebrow">Last configuration change</dt>
          <dd className="mt-1 text-text-secondary">
            {formatApplicationTime(application.last_configuration_change_at)}
          </dd>
        </div>
        <div>
          <dt className="eyebrow">Last authorization</dt>
          <dd className="mt-1 text-text-secondary">
            {application.last_authorization_at
              ? formatApplicationTime(application.last_authorization_at)
              : "No authorization yet"}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card p-5 sm:p-6">
      <div className="mb-5 border-b border-border-standard pb-4">
        <h2 className="font-sans text-base font-semibold text-text-primary">
          {title}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-text-muted">{description}</p>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  required,
  type = "text",
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
}) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
        {required ? " *" : ""}
      </label>
      <input
        id={id}
        type={type}
        className="input"
        value={value}
        placeholder={placeholder}
        required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <FieldError id={`${id}-error`} message={error} />
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-text-disabled">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function TextArea({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  required,
  rows,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  rows: number;
}) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
        {required ? " *" : ""}
      </label>
      <textarea
        id={id}
        rows={rows}
        className="input font-mono text-xs"
        value={value}
        required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <FieldError id={`${id}-error`} message={error} />
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-text-disabled">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function ColorField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <div className="flex gap-2">
        <input
          id={id}
          type="color"
          className="h-10 w-12 cursor-pointer rounded border border-border-standard bg-surface-container-lowest p-1"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
          onChange={(event) => onChange(event.target.value)}
        />
        <input
          aria-label={`${label} hexadecimal value`}
          className="input font-mono"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  error,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  error?: string;
}) {
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        className="input font-mono"
        min={min}
        max={max}
        step={1}
        value={value}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {error ? (
        <FieldError id={`${id}-error`} message={error} />
      ) : (
        <p className="mt-1 text-xs text-text-disabled">
          {min.toLocaleString()}–{max.toLocaleString()}
        </p>
      )}
    </div>
  );
}

function FieldError({ id, message }: { id?: string; message: string }) {
  return (
    <p id={id} role="alert" className="mt-1 text-xs text-error">
      {message}
    </p>
  );
}

function formatApplicationTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
}
